// Buyer <-> seller messages about one lot (server: /api/messages).
import apiClient from './api'

export interface Conversation {
  id: string
  auctionId: string
  auctionTitle: string
  auctionImage: string
  auctionStatus: string
  role: 'buyer' | 'seller'
  counterpart: { id: string; name: string; avatar: string }
  lastMessage: string
  // Photos in the last message ("Photo" stands in when it had no text).
  lastPhotos: number
  lastMessageAt: string | null
  lastFromMe: boolean
  unread: number
}

// A signed, participant-only link (valid for about an hour) with the
// photo's size, so the bubble can reserve its shape before it loads.
export interface ChatPhoto {
  url: string
  width: number | null
  height: number | null
}

export interface ChatMessage {
  id: string
  body: string
  photos: ChatPhoto[]
  createdAt: string
  fromMe: boolean
  senderId: string
}

export const messageService = {
  async listConversations(): Promise<{ data: Conversation[]; unread: number }> {
    const res = await apiClient.get<{ success: boolean; data: Conversation[]; unread: number }>('/api/messages/conversations')
    return { data: res.data?.data || [], unread: res.data?.unread || 0 }
  },

  async unreadCount(): Promise<number> {
    const res = await apiClient.get<{ success: boolean; data: { unread: number } }>('/api/messages/unread-count')
    return res.data?.data?.unread || 0
  },

  /** Opens the thread for a lot (creating it on first use), optionally with the first message. */
  async openForAuction(auctionId: string, body?: string): Promise<{ conversationId: string }> {
    const res = await apiClient.post<any>('/api/messages/conversations', { auctionId, body })
    const d = res.data?.data
    if (res.data?.success === false) throw new Error(res.data?.error || '')
    return { conversationId: d?.conversationId || d?.id }
  },

  async getThread(id: string): Promise<{ conversation: Conversation; messages: ChatMessage[] }> {
    const res = await apiClient.get<any>(`/api/messages/conversations/${id}`)
    if (!res.data?.data) throw new Error(res.data?.error || '')
    return res.data.data
  },

  async send(id: string, body: string): Promise<ChatMessage> {
    const res = await apiClient.post<any>(`/api/messages/conversations/${id}/messages`, { body })
    if (!res.data?.data?.message) throw new Error(res.data?.error || '')
    return res.data.data.message
  },

  /** Up to four photos, with an optional caption, as one message. */
  async sendPhotos(id: string, files: File[], body: string): Promise<ChatMessage> {
    const form = new FormData()
    for (const f of files) form.append('photos', f, f.name)
    if (body) form.append('body', body)
    const res = await apiClient.post<any>(`/api/messages/conversations/${id}/photos`, form)
    if (!res.data?.data?.message) throw new Error(res.data?.error || '')
    return res.data.data.message
  },

  async markRead(id: string): Promise<number> {
    const res = await apiClient.post<any>(`/api/messages/conversations/${id}/read`, {})
    return res.data?.data?.unread ?? 0
  },
}
